// src/utils/signatories.js
// ─────────────────────────────────────────────────────────────────────────────
// WHO SIGNS THE UNDERTAKING?
//
// The declaration's Name is NEVER typed and NEVER pulled from the login profile —
// it is derived from the applicant name(s) entered on Page 1 ("Name of the
// applicant"). Sections belonging to some OTHER party (contact person,
// authorised representative, agent, trader/supplier…) are never signatories.
//
// This lived inside DynamicFormRenderer, which is the screen that FILLS a form.
// The screen that READS one back — the submitted-application view, built by
// services/formService — had no access to it, so it showed
// "Name : N/A" on every form whose declaration states the signatory
// inside the undertaking sentence ("I/We ______ undertake that …") instead of
// giving it a Name field. Form E does exactly that.
//
// One module, so the name on the screen the applicant approves is the name on
// the document that is filed.
// ─────────────────────────────────────────────────────────────────────────────

import { isDeclarationSection } from './undertaking';
import { collectRepeatableDefs } from './repeatableData';

const SIGNATORY_EXCLUDE_RE = /contact\s*person|authoriz(?:ed|ing)?\s*representative|representative|\bagent\b|institution|institute|traditional\s*knowledge|knowledge\s*holder|collaborator|\btrader\b|supplier|recipient/i;

const ENTITY_NAME_KEYS = [
  'applicant_name', 'name_of_applicant', 'name_of_the_applicant', 'entity_name',
  'name_of_entity', 'organisation_name', 'organization_name', 'company_name',
  'institution_name',
];

/* Field names are compared NORMALISED — case, spaces, underscores and hyphens
   stripped — never literally. The guessed key list above is written in
   snake_case, but a template is free to call the very same field
   `applicantName`, `ApplicantName` or `applicant name`; an exact-string lookup
   matched none of those, the applicant name resolved to '', and every screen
   that derives the signatory from it went blank. That is what left Form E's
   "I/We ______ undertake that …" un-auto-filled. */
const nkey = s => String(s ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

/* value bag keyed by normalised field name; first writer wins so the template's
   own key order decides ties. */
const normalizedBag = (src = {}) => {
  const bag = {};
  Object.entries(src || {}).forEach(([k, v]) => {
    const n = nkey(k);
    if (n && bag[n] === undefined) bag[n] = v;
  });
  return bag;
};

/* A field whose LABEL says it holds the applicant's own name. Labels are the
   one thing that does not change when a template is re-keyed, so this is how
   the applicant name is found on a form nobody has hard-coded keys for.
   `institution` is deliberately absent: SIGNATORY_EXCLUDE_RE treats it as
   another party. */
const APPLICANT_NAME_LABEL_RE =
  /name\s*(?:of|for)\s*(?:the\s*)?(?:applicant|depositor|firm|company|organisation|organization|entity)|(?:applicant|depositor)(?:['’]s)?\s*name/i;

/**
 * The field name(s) a template uses for "Name of the applicant", found by LABEL.
 *
 * The applicant name used to be looked up under a fixed list of guessed keys.
 * Any form that named the field something else — Form E's Page 1 "Name of the
 * applicant (Depositor)" — resolved to no name at all, so the declaration's
 * in-sentence blank was never auto-filled and the summary could not say who
 * signed. Asking the template what its own applicant-name field is called
 * removes the guess.
 *
 * @param {Array} pages  template pages ($refs already resolved)
 * @returns {string[]}   field names, in template order
 */
export function applicantNameKeys(pages) {
  const keys = [];
  const walk = fields => (fields || []).forEach(f => {
    if (!f) return;
    if (Array.isArray(f.fields) && f.fields.length) { walk(f.fields); return; }
    if (!f.name) return;
    const label = String(f.label || f.title || '');
    if (!APPLICANT_NAME_LABEL_RE.test(label)) return;
    if (SIGNATORY_EXCLUDE_RE.test(label)) return;       // some other party's name
    if (!keys.includes(f.name)) keys.push(f.name);
  });
  (pages || []).forEach(pg => (pg?.sections || []).forEach(sec => {
    if (!sec || typeof sec !== 'object') return;
    if (SIGNATORY_EXCLUDE_RE.test(String(sec.sectionName || ''))) return;
    // A declaration's own Name row is the OUTPUT of this lookup, never its source.
    if (isDeclarationSection(sec)) return;
    walk(sec.fields);
  }));
  return keys;
}

/**
 * "Dr Prabhu B. Patil" from a row of name parts, or the entity's own name.
 *
 * @param {object} src        a values bag (flat form data, or one repeatable row)
 * @param {string[]} nameKeys field names this template calls the applicant name
 *                            (from applicantNameKeys) — consulted first, because
 *                            they are what the form itself says, not a guess.
 */
export const signatoryFullName = (src = {}, nameKeys = []) => {
  const bag = normalizedBag(src);
  const get = k => String(bag[nkey(k)] ?? '').trim();
  for (const k of nameKeys || []) { const v = get(k); if (v) return v; }
  const person = ['first_name', 'middle_name', 'last_name']
    .map(get).filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
  if (person) return person;
  for (const k of ENTITY_NAME_KEYS) { const v = get(k); if (v) return v; }
  return '';
};

/**
 * One entry per applicant: repeatable "Applicant Details" rows first; flat forms
 * fall back to the top-level applicant name fields.
 *
 * @param {object} values     flat form values (or the saved flat data)
 * @param {object} rowsByKey  repeatable section name → saved rows[]
 * @param {string[]} nameKeys applicant-name field names from applicantNameKeys
 */
export function collectApplicantNames(values = {}, rowsByKey = {}, nameKeys = []) {
  const names = [];
  const known = new Set([...(nameKeys || []), ...ENTITY_NAME_KEYS, 'first_name', 'last_name'].map(nkey));
  Object.entries(rowsByKey || {}).forEach(([rk, rows]) => {
    if (!Array.isArray(rows) || SIGNATORY_EXCLUDE_RE.test(rk)) return;
    rows.forEach(row => {
      if (!row || typeof row !== 'object') return;
      if (!Object.keys(row).some(k => known.has(nkey(k)))) return;
      const n = signatoryFullName(row, nameKeys);
      if (n) names.push(n);
    });
  });
  if (!names.length) { const n = signatoryFullName(values, nameKeys); if (n) names.push(n); }
  return names;
}

/* Form-state key for the i-th applicant's declaration signature. The first
   applicant keeps the legacy 'declarationSignature' key so existing saved
   drafts round-trip unchanged. */
export const declSigKey = i => i > 0 ? `declarationSignature__${i}` : 'declarationSignature';

/* Per-signer editable name override. Defaults to the Page-1 auto-fetched name,
   but each signatory can be corrected in the signature panel (multi-applicant). */
export const declSignerNameKey = i => `declSignerName__${i}`;

/** The signatory names to persist/preview: the Page-1 name unless overridden. */
export const effectiveSignatoryNames = (values = {}, rowsByKey = {}, nameKeys = []) =>
  collectApplicantNames(values, rowsByKey, nameKeys).map((nm, i) => {
    const o = values[declSignerNameKey(i)];
    return (o !== undefined && String(o).trim() !== '') ? String(o).trim() : nm;
  });

/**
 * Only the rows of the repeatable sections THIS template declares.
 *
 * The signatory is whoever Page 1 names — the sections the applicant can see.
 * The rows a caller holds can carry more than that: the live form state and a
 * saved record both keep arrays the current form no longer shows (rows saved
 * under a renumbered or other-category section name, a leftover from an earlier
 * build). Reading every array counted the same applicant once per copy, and the
 * declaration's Name read "A B, A B" over a Page 1 that lists one applicant.
 * With no template to judge by, the rows are returned unfiltered.
 *
 * @param {object} rowsByKey  section name → rows[]
 * @param {Array}  pages      the template pages the applicant is filling
 */
export function templateRowSets(rowsByKey = {}, pages = []) {
  const keys = collectRepeatableDefs(pages).map(r => r.key);
  if (!keys.length) return rowsByKey || {};
  const out = {};
  keys.forEach(k => { if (Array.isArray(rowsByKey?.[k])) out[k] = rowsByKey[k]; });
  return out;
}

/* Comparable form of one name, and of a joined "A B, C D" list. A name that
   itself contains a comma ("XYZ Pvt, Ltd") splits into parts Page 1 does not
   list, so such a value is simply treated as typed — never overwritten. */
const normName  = s => String(s ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
const nameParts = v => String(v ?? '').split(',').map(normName).filter(Boolean);

/**
 * Was this Name written by the auto-fill rather than typed by the applicant?
 *
 * It is when every part of it is one of the Page 1 applicant names — even if it
 * no longer matches them exactly ("A B, A B" after a duplicate was counted, or
 * "A B" after a second applicant was added). Such a value is a stale copy of
 * the list, and must follow Page 1. A name the applicant typed themselves
 * ("Dr. A B", "A B for XYZ Ltd") contains something Page 1 does not say, and is
 * never touched.
 *
 * @param {string}   value  the Name as stored
 * @param {string[]} names  the applicant names Page 1 lists now
 */
export function isAutoSignatoryName(value, names = []) {
  const parts = nameParts(value);
  if (!parts.length) return true;                      // blank: ours to fill
  const known = new Set((names || []).map(normName).filter(Boolean));
  return known.size > 0 && parts.every(p => known.has(p));
}

/**
 * The Name a declaration should show: the Page 1 applicant names, unless the
 * stored value is one the applicant typed for themselves (isAutoSignatoryName).
 */
export const declarationNameFor = (stored, names = []) => {
  const list = (names || []).map(n => String(n ?? '').trim()).filter(Boolean);
  if (!list.length) return String(stored ?? '').trim();
  return isAutoSignatoryName(stored, list) ? list.join(', ') : String(stored ?? '').trim();
};

/* ─────────────────────────────────────────────────────────────────────────────
   READING A SIGNATURE BACK

   The live form and the SAVED record do not call a signature the same thing:

     live form state   declarationSignature, declarationSignature__1, __2 …
     saved record      the declaration's own signature field for applicant 1,
                       then signature_applicant_2, signature_applicant_3 …
                       (see the declaration serialiser in DynamicFormRenderer)

   Reading a submitted application back looked only for the LIVE keys, so every
   signature resolved to '' and a multi-applicant declaration printed "N/A"
   against every signatory in the preview, however many
   of them had actually signed. Both spellings are accepted here, at the one
   point every reader goes through.
───────────────────────────────────────────────────────────────────────────── */

const hasSig = v => v != null && (typeof v === 'object' || String(v).trim() !== '');

/* The declaration's OWN signature field — where applicant 1's signature is
   saved. Matched by field name first, then by an upload field whose label says
   "signature", so a template free to call it `signature_of_applicant` resolves
   just as well. */
const SIG_FIELD_NAME_RE = /^(?:declaration)?sign(?:ature)?(?:ofapplicant|applicant1?)?$/;

export function ownDeclarationSignature(section, values = {}) {
  const leaves = [];
  const walk = fields => (fields || []).forEach(f => {
    if (!f) return;
    if (Array.isArray(f.fields) && f.fields.length) { walk(f.fields); return; }
    if (f.name) leaves.push(f);
  });
  walk(section?.fields);
  const f = leaves.find(x => SIG_FIELD_NAME_RE.test(nkey(x.name)))
    || leaves.find(x => (x.type === 'upload' || x.type === 'file') && /signature/i.test(String(x.label || '')));
  if (!f) return '';
  const v = values?.[f.name];
  return hasSig(v) ? v : (hasSig(f.value) ? f.value : '');
}

/**
 * The i-th signatory's signature, in whichever shape the data holds it.
 *
 * @param {object} values      flat values (live form state or saved record)
 * @param {number} i           signatory index, 0-based
 * @param {*} [ownSignature]   the declaration's own signature field value
 */
export function signerSignature(values = {}, i = 0, ownSignature = '') {
  const live = values?.[declSigKey(i)];
  if (hasSig(live)) return live;

  const bag = normalizedBag(values);
  const saved = bag[nkey(`signature_applicant_${i + 1}`)];
  if (hasSig(saved)) return saved;

  if (i === 0) {
    if (hasSig(ownSignature)) return ownSignature;
    /* These exact spellings only, never "any key containing signature": the bag
       here is the whole flat record, which also carries other parties' signature
       fields (authorised representative, witness …). */
    for (const k of ['declarationsignature', 'signature', 'sign']) {
      if (hasSig(bag[k])) return bag[k];
    }
  }
  return '';
}

/**
 * Build the `signatories` list a built section carries into the preview.
 * Mirrors what DynamicFormRenderer attaches while the form is being filled, so
 * the read-back of a submitted application says the same thing.
 *
 * @param {object} values     flat saved values
 * @param {object} rowsByKey  repeatable section name → rows[]
 * @param {Function} [toSignature]  (key, index) → the signature to display.
 *        Defaults to signerSignature, which reads both the live-form and the
 *        saved spellings — pass one only to override that.
 * @param {string[]} nameKeys  applicant-name field names from applicantNameKeys
 * @param {*} [ownSignature]   the declaration's own signature field value
 *        (ownDeclarationSignature), where applicant 1's signature is saved
 */
export const buildSignatories = (values = {}, rowsByKey = {}, toSignature = null, nameKeys = [], ownSignature = '') =>
  effectiveSignatoryNames(values, rowsByKey, nameKeys)
    .map((name, i) => ({
      name,
      signature: (toSignature ? toSignature(declSigKey(i), i) : signerSignature(values, i, ownSignature)) || '',
    }));

/* Field names that hold a declaration's own "Name" answer, normalised. */
const NAME_FIELD_KEYS = ['name', 'nameofdeclarant', 'declarantname', 'nameofthedeclarant', 'signatoryname'];

/** Is `field` a declaration's own "Name" answer? */
export const isDeclarationNameField = field => !!field && NAME_FIELD_KEYS.includes(nkey(field.name));

/**
 * The name to print on a declaration's "Name" row.
 *
 * In order: what the declaration itself collected, then the signatory list built
 * from Page 1. A form that states the signatory inside the undertaking sentence
 * has no Name field at all — that is the case the fallback exists for, and
 * without it the filed document says "N/A" where the applicant's name belongs.
 *
 * @param {object} section  a built filledPages declaration section
 * @returns {string}
 */
export function resolveDeclarantName(section) {
  const fields = Array.isArray(section?.fields) ? section.fields : [];
  const own = fields.find(f => f && NAME_FIELD_KEYS.includes(nkey(f.name)));
  const ownValue = String(own?.value ?? '').trim();
  if (ownValue) return ownValue;

  const sigs = Array.isArray(section?.signatories) ? section.signatories.filter(s => s && s.name) : [];
  if (sigs.length) return sigs.map(s => String(s.name).trim()).filter(Boolean).join(', ');
  return '';
}

export default collectApplicantNames;
